"use client";

import { retryWithProjectCacheEviction } from "@/lib/aicheck/cache-eviction";
import { syncProjectCacheInventory } from "@/lib/aicheck/cache-inventory";
import { getCheckpointCacheKey } from "@/lib/aicheck/cache-keys";
import { normalizeNodeExecutionDirectives } from "@/lib/aicheck/node-execution";
import type {
  AICheckRunCheckpoint,
  CacheStaleReason,
  CheckpointAIResultEntry,
  OutputLanguage,
} from "@/types/aicheck";

export const CHECKPOINT_CACHE_VERSION = 1;

interface StoredCheckpoint {
  v: number;
  data: AICheckRunCheckpoint;
}

export interface CheckpointGetResult {
  checkpoint: AICheckRunCheckpoint | null;
  staleReason: CacheStaleReason | null;
}

function buildEmptyCheckpoint(
  projectId: number,
  language: OutputLanguage,
  startedAt: string,
): AICheckRunCheckpoint {
  const updatedAt = new Date().toISOString();
  return {
    projectId,
    language,
    startedAt,
    updatedAt,
    completedCheckIds: [],
    aiResults: [],
    consistencyResults: [],
  };
}

function normalizeAIResults(
  entries: CheckpointAIResultEntry[],
): CheckpointAIResultEntry[] {
  const next = new Map<string, CheckpointAIResultEntry>();
  for (const entry of entries) {
    const checkId = entry.result?.checkId;
    if (!checkId) continue;
    next.set(checkId, entry);
  }
  return [...next.values()];
}

function normalizeCheckpoint(
  checkpoint: AICheckRunCheckpoint,
): AICheckRunCheckpoint {
  const aiResults = normalizeAIResults(checkpoint.aiResults ?? []);
  const nodeExecutionDirectives = normalizeNodeExecutionDirectives(
    checkpoint.nodeExecutionDirectives,
  );
  const completedCheckIds = [
    ...new Set(aiResults.map((r) => r.result.checkId)),
  ];
  const consistencyByRuleId = new Map(
    (checkpoint.consistencyResults ?? []).map((result) => [
      result.ruleId,
      result,
    ]),
  );
  return {
    ...checkpoint,
    updatedAt: new Date().toISOString(),
    aiResults,
    completedCheckIds,
    consistencyResults: [...consistencyByRuleId.values()],
    nodeExecutionDirectives:
      nodeExecutionDirectives.length > 0 ? nodeExecutionDirectives : undefined,
    nodeControlVersion: checkpoint.nodeControlVersion ?? 0,
  };
}

function loadStoredCheckpoint(
  projectId: number,
  language: OutputLanguage,
): CheckpointGetResult {
  const key = getCheckpointCacheKey(projectId, language);
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return { checkpoint: null, staleReason: null };
    const parsed = JSON.parse(raw) as StoredCheckpoint;
    if (!parsed || typeof parsed !== "object") {
      localStorage.removeItem(key);
      syncProjectCacheInventory();
      return { checkpoint: null, staleReason: "invalid" };
    }
    if (parsed.v !== CHECKPOINT_CACHE_VERSION) {
      localStorage.removeItem(key);
      syncProjectCacheInventory();
      return { checkpoint: null, staleReason: "version_mismatch" };
    }
    return {
      checkpoint: normalizeCheckpoint(parsed.data),
      staleReason: null,
    };
  } catch {
    try {
      localStorage.removeItem(key);
      syncProjectCacheInventory();
    } catch {
      // Ignore cleanup failures.
    }
    return { checkpoint: null, staleReason: "parse_error" };
  }
}

function saveStoredCheckpoint(checkpoint: AICheckRunCheckpoint): void {
  const normalized = normalizeCheckpoint(checkpoint);
  retryWithProjectCacheEviction(
    () => {
      localStorage.setItem(
        getCheckpointCacheKey(normalized.projectId, normalized.language),
        JSON.stringify({
          v: CHECKPOINT_CACHE_VERSION,
          data: normalized,
        } satisfies StoredCheckpoint),
      );
      syncProjectCacheInventory();
    },
    { protectedProjectId: normalized.projectId },
  );
}

export function getCheckpoint(
  projectId: number,
  language: OutputLanguage,
): CheckpointGetResult {
  return loadStoredCheckpoint(projectId, language);
}

export function saveCheckpoint(
  checkpoint: AICheckRunCheckpoint,
): AICheckRunCheckpoint {
  saveStoredCheckpoint(checkpoint);
  return normalizeCheckpoint(checkpoint);
}

export function updateCheckpoint(
  params: {
    projectId: number;
    language: OutputLanguage;
    startedAt?: string;
  },
  updater: (current: AICheckRunCheckpoint) => AICheckRunCheckpoint,
): AICheckRunCheckpoint {
  const current =
    loadStoredCheckpoint(params.projectId, params.language).checkpoint ??
    buildEmptyCheckpoint(
      params.projectId,
      params.language,
      params.startedAt ?? new Date().toISOString(),
    );
  const next = updater(current);
  saveStoredCheckpoint(next);
  return normalizeCheckpoint(next);
}

export function clearCheckpoint(
  projectId: number,
  language: OutputLanguage,
): void {
  try {
    localStorage.removeItem(getCheckpointCacheKey(projectId, language));
    syncProjectCacheInventory();
  } catch {
    // Ignore storage failures while clearing.
  }
}

export function deriveRemainingCheckIds(
  checkpoint: AICheckRunCheckpoint,
): string[] {
  const completed = new Set(checkpoint.completedCheckIds ?? []);
  return (checkpoint.plannedCheckIds ?? []).filter(
    (checkId) => !completed.has(checkId),
  );
}
